import {
  EXPENSE_CATEGORIES,
  type ExpenseMethod,
  expenseProblem,
  formatRupees,
  localDay,
  MAX_EXPENSE_FILES,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  toPaise,
} from '@quickreport/domain';
import { Button, Field, Input, Modal, Select, Textarea } from '@quickreport/ui';
import { addExpense, deleteExpense, type ExpenseRow, updateExpense } from '@quickreport/workspace';
import { ImagePlus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { describeError } from '../../lib/api';
import { useFileUrls } from '../../lib/file-urls';
import { useWorkspace } from '../../lib/workspace';
import { ImageError, storeImage } from '../../local-db/files';
import { useCtx } from '../reports/context';

/** A picture chosen in this dialog that has not been saved yet. */
interface Pending {
  key: string;
  file: File;
  url: string;
}

const AMOUNT = /^\d{0,9}(\.\d{0,2})?$/;

/**
 * Add or change an expense. Bills are photos or screenshots: choose them, take one with the
 * phone camera, drop them in, or paste a screenshot. They are shrunk and kept only when you save.
 */
export function ExpenseDialog({
  expense,
  categories,
  onClose,
  onSaved,
}: {
  expense: ExpenseRow | null;
  categories: string[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const editing = expense !== null;
  const [spentOn, setSpentOn] = useState(expense?.spent_on ?? localDay());
  const [amount, setAmount] = useState(expense ? String(Number(expense.amount_paise) / 100) : '');
  const [category, setCategory] = useState(expense?.category ?? '');
  const [vendor, setVendor] = useState(expense?.vendor ?? '');
  const [method, setMethod] = useState<ExpenseMethod>((expense?.method as ExpenseMethod) ?? 'cash');
  const [note, setNote] = useState(expense?.note ?? '');
  const [kept, setKept] = useState<string[]>(expense?.file_ids ?? []);
  const [added, setAdded] = useState<Pending[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const urls = useFileUrls(kept);
  const options = useMemo(() => [...new Set([...EXPENSE_CATEGORIES, ...categories])], [categories]);
  const total = kept.length + added.length;

  useEffect(
    () => () => {
      for (const a of added) URL.revokeObjectURL(a.url);
    },
    [added],
  );

  function take(files: File[]) {
    setError(null);
    const pictures = files.filter((f) => f.type.startsWith('image/'));
    if (pictures.length < files.length) setError('Only pictures and screenshots can be attached.');
    const room = MAX_EXPENSE_FILES - total;
    if (pictures.length > room)
      setError(`You can attach up to ${MAX_EXPENSE_FILES} pictures to one expense.`);
    setAdded((a) => [
      ...a,
      ...pictures.slice(0, Math.max(0, room)).map((file) => ({
        key: `${file.name}-${file.size}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  }

  // Paste a screenshot anywhere in the dialog.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `take` only reads the current count
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])].filter((f) => f.type.startsWith('image/'));
      if (files.length) {
        e.preventDefault();
        take(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [total]);

  const paise = toPaise(Number(amount) || 0);
  // Said beside the amount once it is left or Save is pressed, not only as a message below.
  const [amountSeen, setAmountSeen] = useState(false);
  const amountError = amountSeen && !(paise > 0) ? 'Enter an amount more than zero' : null;

  async function save() {
    setError(null);
    const base = {
      spentOn,
      amountPaise: paise,
      category,
      vendor,
      method,
      note,
    };
    setAmountSeen(true);
    const problem = expenseProblem({ ...base, fileIds: [...kept, ...added.map((a) => a.key)] });
    if (problem) return setError(problem);
    setBusy(true);
    try {
      const ids = [...kept];
      for (const a of added) {
        ids.push(await storeImage(ws.db, ws.orgId, a.file, { maxSide: 1600, jpeg: true }));
      }
      if (editing)
        await updateExpense(ws.db, ctx, (expense as ExpenseRow).id, { ...base, fileIds: ids });
      else await addExpense(ws.db, ctx, { ...base, fileIds: ids });
      void ws.engine.syncNow();
      onSaved(editing ? 'Expense saved.' : `Expense of ${formatRupees(paise)} added.`);
    } catch (err) {
      setError(err instanceof ImageError ? err.message : describeError(err));
      setBusy(false);
    }
  }

  async function remove() {
    if (!expense) return;
    setBusy(true);
    try {
      await deleteExpense(ws.db, ctx, expense.id);
      void ws.engine.syncNow();
      onSaved('Expense removed.');
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <Modal title={editing ? 'Change expense' : 'Add an expense'} label="Expense" onClose={onClose}>
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (₹)" error={amountError}>
            <Input
              autoFocus
              inputMode="decimal"
              value={amount}
              placeholder="0"
              onChange={(e) => AMOUNT.test(e.target.value) && setAmount(e.target.value)}
              onBlur={() => setAmountSeen(true)}
            />
          </Field>
          <Field label="Date">
            <Input
              type="date"
              value={spentOn}
              max={localDay()}
              onChange={(e) => setSpentOn(e.target.value)}
            />
          </Field>
        </div>
        <Field label="What was it for?" hint="Choose a kind, or type your own.">
          <Input
            list="expense-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Reagents and consumables"
          />
          <datalist id="expense-categories">
            {options.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Paid to (optional)">
            <Input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Sample Suppliers"
            />
          </Field>
          <Field label="Paid by">
            <Select value={method} onChange={(e) => setMethod(e.target.value as ExpenseMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Note (optional)">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        <div className="grid gap-2">
          <div className="flex items-baseline justify-between">
            <span className="text-[12px] font-medium text-slate">
              Bill or screenshot (optional)
            </span>
            <span className="text-[12px] text-slate">
              {total} of {MAX_EXPENSE_FILES}
            </span>
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: a drop target; the button inside does the same for the keyboard */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              take([...e.dataTransfer.files]);
            }}
            className={`grid gap-2 rounded-lg border border-dashed p-3 text-center ${over ? 'border-accent bg-accent-soft' : 'border-line bg-canvas'}`}
          >
            <input
              ref={input}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="sr-only"
              aria-label="Choose pictures of the bill"
              tabIndex={-1}
              onChange={(e) => {
                take([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
            <Button
              size="sm"
              className="mx-auto"
              disabled={total >= MAX_EXPENSE_FILES}
              onClick={() => input.current?.click()}
            >
              <ImagePlus size={14} /> Add a photo or screenshot
            </Button>
            <span className="text-[12px] text-slate">
              or drop pictures here, or paste a screenshot (Ctrl+V)
            </span>
          </div>
          {total ? (
            <ul className="grid grid-cols-5 gap-2">
              {kept.map((id) => (
                <li
                  key={id}
                  className="relative aspect-square overflow-hidden rounded-lg border border-line bg-canvas"
                >
                  {urls[id] ? (
                    <img
                      src={urls[id] as string}
                      alt="Bill"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="grid h-full place-items-center px-1 text-center text-[10px] text-slate">
                      Arriving…
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label="Remove this picture"
                    className="absolute top-0.5 right-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white"
                    onClick={() => setKept((k) => k.filter((x) => x !== id))}
                  >
                    <X size={11} />
                  </button>
                </li>
              ))}
              {added.map((a) => (
                <li
                  key={a.key}
                  className="relative aspect-square overflow-hidden rounded-lg border border-line bg-canvas"
                >
                  <img src={a.url} alt="Bill to be saved" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    aria-label="Remove this picture"
                    className="absolute top-0.5 right-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white"
                    onClick={() => setAdded((x) => x.filter((p) => p.key !== a.key))}
                  >
                    <X size={11} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
            {error}
          </p>
        ) : null}
        {confirmDelete ? (
          <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
            Remove this expense?
            <Button size="sm" tone="danger" disabled={busy} onClick={() => void remove()}>
              Yes, remove
            </Button>
            <Button size="sm" tone="ghost" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          {editing ? (
            <Button tone="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={busy}>
              <Trash2 size={13} /> Remove
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button tone="primary" type="submit" disabled={busy || paise <= 0 || !category.trim()}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Add expense'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
